<template>
  <app-layout>
    <div class="sc-container">

      <div class="detail-layout">
        <!-- MAIN COLUMN -->
        <div class="detail-main">

          <!-- Title bar -->
          <div class="sc-card">
            <div class="sc-card-body title-bar">
              <div class="title-bar-left">
                <h1 class="detail-title">{{ details.service }}</h1>
                <div class="detail-meta">
                  <span v-if="details.city || details.state || details.country" class="meta-loc">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {{ details.city ? details.city.name+', ' : '' }}{{ details.state ? details.state.name+', ' : '' }}{{ details.country ? details.country.name : '' }}
                  </span>
                  <span class="meta-date">Posted {{ formatDate(details.created_at) }}</span>
                  <span v-if="details.end_date" class="svc-deadline">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    Deadline: {{ formatDate2(details.end_date) }}
                  </span>
                </div>
              </div>
              <div class="title-bar-right">
                <button
                  v-if="id == details.user_id"
                  class="btn-outline"
                  @click="showEditModal = true"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit
                </button>
              </div>
            </div>
          </div>

          <!-- Tabs -->
          <div class="sc-card pd-tabs-bar">
            <div class="filter-tabs">
              <button class="filter-tab" :class="{ active: activeTab === 'overview' }" @click="activeTab = 'overview'">Overview</button>
              <button v-if="id == details.user_id" class="filter-tab" :class="{ active: activeTab === 'applicants' }" @click="activeTab = 'applicants'">
                Applicants
                <span v-if="details.applicants && details.applicants.length" class="pd-tab-count">{{ details.applicants.length }}</span>
              </button>
            </div>
          </div>

          <!-- Overview Tab -->
          <div v-if="activeTab === 'overview'">
            <div class="sc-card">
              <div class="sc-card-header">Description</div>
              <div class="sc-card-body prose-content" v-html="details.description"></div>
            </div>

            <div class="sc-card" style="margin-top:1rem;">
              <div class="sc-card-header">Service Details</div>
              <div class="sc-card-body">
                <div class="details-grid">
                  <div class="detail-row">
                    <span class="detail-label">Service Needed</span>
                    <span class="detail-value">{{ details.service || '--' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Job Location</span>
                    <span class="detail-value">{{ details.city ? details.city.name+', ' : '' }}{{ details.state ? details.state.name+', ' : '' }}{{ details.country ? details.country.name : '--' }}</span>
                  </div>
                  <div v-if="details.address" class="detail-row">
                    <span class="detail-label">Address</span>
                    <span class="detail-value">{{ details.address }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Application Deadline</span>
                    <span class="detail-value">{{ details.end_date ? formatDate2(details.end_date) : '--' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Applicants Tab -->
          <div v-if="activeTab === 'applicants'">
            <div class="sc-card">
              <div class="sc-card-header">Applicants</div>
              <div class="sc-card-body">
                <div v-if="details.applicants && details.applicants.length" class="applicants-list">
                  <div v-for="(data, index) in details.applicants" :key="index" class="applicant-item">
                    <div class="applicant-avatar">
                      <img v-if="data.user && data.user.avatar" :src="image_url+'users/'+data.user.avatar" :alt="data.user.name" />
                      <img v-else src="/assets/avatar.png" alt="" />
                    </div>
                    <div class="applicant-info">
                      <router-link
                        v-if="data.user && data.user.type === 'user'"
                        :to="'/sc/profile/'+data.user.slug"
                        class="applicant-name"
                      >{{ data.user.name }}</router-link>
                      <router-link
                        v-else-if="data.user && data.user.company"
                        :to="'/company/'+data.user.company.slug"
                        class="applicant-name"
                      >{{ data.user.name }}</router-link>
                      <span v-if="data.user && data.user.profession" class="applicant-role">{{ data.user.profession }}</span>
                      <span v-if="data.user && data.user.country" class="applicant-loc">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {{ data.user.country ? data.user.country.name : '' }}{{ data.user.state ? ', '+data.user.state.name : '' }}
                      </span>
                    </div>
                  </div>
                </div>
                <p v-else class="no-content">No applicants yet.</p>
              </div>
            </div>
          </div>

        </div>

        <!-- ASIDE COLUMN -->
        <aside class="detail-aside">

          <!-- Poster card -->
          <div v-if="details.user" class="author-card">
            <div class="author-avatar">
              <img v-if="details.user.avatar" :src="image_url+'users/'+details.user.avatar" :alt="details.user.name" />
              <img v-else src="/assets/avatar.png" alt="" />
            </div>
            <div class="author-name">{{ details.user.name }}</div>
            <div class="author-role">
              <template v-if="details.user.currentcompany">
                {{ details.user.current_position }} at {{ details.user.currentcompany.name }}
              </template>
              <template v-else>{{ details.user.profession }}</template>
            </div>
            <div v-if="details.user.state || details.user.country" class="author-loc">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {{ details.user.state ? details.user.state.name+', ' : '' }}{{ details.user.country ? details.user.country.name : '' }}
            </div>
            <div class="author-actions">
              <a
                :href="details.user.type === 'company' && details.user.company ? '/company/'+details.user.company.slug : '/sc/profile/'+details.user.slug"
                class="btn-outline"
                target="_blank"
              >View Profile</a>
            </div>
          </div>

          <!-- Apply card (non-owner) -->
          <div v-if="id != details.user_id" class="sc-card">
            <div class="sc-card-body" style="text-align:center">
              <p class="svc-apply-prompt">Think you can handle this service?</p>
              <button v-if="has_applied > 0" class="btn-outline svc-applied-btn" disabled>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Applied
              </button>
              <button v-else class="btn-primary" style="width:100%" @click="showProvideModal = true">
                Provide Service
              </button>
            </div>
          </div>

          <!-- Ads -->
          <div v-if="adverts.length" class="sc-card">
            <div class="sc-card-body">
              <div v-for="ad in adverts" :key="ad.id" class="ad-card">
                <a :href="ad.link" target="_blank">
                  <img :src="image_url+'advert/'+ad.image" :alt="ad.link" class="ad-img" />
                </a>
              </div>
            </div>
          </div>

        </aside>
      </div>
    </div>

    <!-- Edit Service Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="showEditModal = false">
      <div class="modal-panel">
        <div class="modal-header">
          <h2>Update Service Details</h2>
          <button class="modal-close" @click="showEditModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="sc-field">
            <label>Service Provider Needed <span class="req">*</span></label>
            <select v-model="details.service">
              <option value="" disabled>Select Service</option>
              <option v-for="(p, i) in professions" :key="i" :value="p.name">{{ p.name }}</option>
            </select>
          </div>
          <div class="sc-field">
            <label>Job Description <span class="req">*</span></label>
            <QuillEditor v-model:content="details.description" contentType="html" :toolbar="customToolbar" />
          </div>
          <div class="form-section-label">Project Location</div>
          <div class="fields-row">
            <div class="sc-field">
              <label>Country</label>
              <select v-model="details.country_id" @change="fetchStates($event)">
                <option value="" disabled>Country</option>
                <option v-for="c in country" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="sc-field">
              <label>State</label>
              <select v-model="details.state_id" @change="fetchCities($event)">
                <option value="" disabled>State</option>
                <option v-for="s in state" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>
            <div class="sc-field">
              <label>City</label>
              <select v-model="details.city_id">
                <option value="" disabled>City</option>
                <option v-for="c in city" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
          </div>
          <div class="sc-field">
            <label>Address</label>
            <textarea v-model="details.address" rows="3"></textarea>
          </div>
          <div class="sc-field">
            <label>Application Deadline</label>
            <input type="date" v-model="details.end_date" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="showEditModal = false">Cancel</button>
          <button class="btn-primary" :disabled="loading" @click="updateRequest">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Update Request</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Provide Service Confirm Modal -->
    <div v-if="showProvideModal" class="modal-overlay" @click.self="showProvideModal = false">
      <div class="modal-panel" style="max-width:420px">
        <div class="modal-header">
          <h2>Confirm Application</h2>
          <button class="modal-close" @click="showProvideModal = false">&times;</button>
        </div>
        <div class="modal-body" style="text-align:center; padding:2rem 1.5rem">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" stroke-width="1.5" style="margin-bottom:1rem"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          <p style="margin:0; color:#374151;">Click <strong>Confirm</strong> to apply to this service.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="showProvideModal = false">Cancel</button>
          <button class="btn-primary" :disabled="loading" @click="provideService">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Confirm</span>
          </button>
        </div>
      </div>
    </div>

  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout.vue';
import axios from 'axios';
import moment from 'moment';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { useSEO } from '@/utils/seo.js';

export default {
  name: 'ServiceDetail',
  components: { AppLayout, QuillEditor },

  data() {
    return {
      details: {},
      has_applied: null,
      loading: false,
      id: localStorage.getItem('user'),
      activeTab: 'overview',
      showEditModal: false,
      showProvideModal: false,
      adverts: [],
      professions: [],
      country: [],
      state: [],
      city: [],
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ],
    };
  },

  computed: {
    image_url() {
      return process.env.VUE_APP_IMAGE_URL;
    },
  },

  created() {
    this.fetchDetails();
    this.fetchAdverts();
    this.fetchProfessions();
    this.fetchCountries();
  },

  watch: {
    $route: {
      immediate: true,
      handler(to) {
        const subject = to.params.id.substring(0, to.params.id.length - 18);
        document.title = to.meta.title || subject.replace(/-/g, ' ') + ' - SC Listings';
      },
    },
  },

  mounted() {
    window.Fire.on('action', () => {
      this.fetchDetails();
      this.fetchCountries();
    });
  },

  methods: {
    fetchDetails() {
      axios(process.env.VUE_APP_API_URL + 'service/details/' + this.$route.params.id)
        .then(res => {
          if (res.status !== 200) return;
          this.details = res.data;
          useSEO({ title: res.data.service, description: res.data.description, url: window.location.href });
          this.checkApplication(res.data.id);
          this.fetchStates2(res.data.country_id);
          this.fetchCities2(res.data.state_id);
        }).catch(() => {});
    },

    checkApplication(service) {
      axios(process.env.VUE_APP_API_URL + 'service/check-application/' + service + '/' + localStorage.getItem('user'))
        .then(res => { if (res.status === 200) this.has_applied = res.data; })
        .catch(() => {});
    },

    fetchAdverts() {
      axios(process.env.VUE_APP_API_URL + 'adverts')
        .then(res => { if (res.status === 200) this.adverts = res.data; })
        .catch(() => {});
    },

    fetchProfessions() {
      axios(process.env.VUE_APP_API_URL + 'professions')
        .then(res => { if (res.status === 200) this.professions = res.data; })
        .catch(() => {});
    },

    fetchCountries() {
      axios(process.env.VUE_APP_API_URL + 'country')
        .then(res => { if (res.status === 200) this.country = res.data; })
        .catch(() => {});
    },

    fetchStates(e) {
      axios(process.env.VUE_APP_API_URL + 'state/' + e.target.value)
        .then(res => { if (res.status === 200) this.state = res.data; })
        .catch(() => {});
    },

    fetchCities(e) {
      axios(process.env.VUE_APP_API_URL + 'city/' + e.target.value)
        .then(res => { if (res.status === 200) this.city = res.data; })
        .catch(() => {});
    },

    fetchStates2(id) {
      axios(process.env.VUE_APP_API_URL + 'state/' + id)
        .then(res => { if (res.status === 200) this.state = res.data; })
        .catch(() => {});
    },

    fetchCities2(id) {
      axios(process.env.VUE_APP_API_URL + 'city/' + id)
        .then(res => { if (res.status === 200) this.city = res.data; })
        .catch(() => {});
    },

    updateRequest() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'service/update', this.details)
        .then(res => {
          if (res.status === 200) {
            this.showEditModal = false;
            window.Fire.emit('action');
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    provideService() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'service/provide-service', {
        user_id: localStorage.getItem('user'),
        service_id: this.details.id,
      })
        .then(res => {
          if (res.status === 200) {
            this.has_applied = 1;
            this.showProvideModal = false;
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    formatDate(date) {
      return moment(date).fromNow();
    },

    formatDate2(date) {
      return moment(date).format('DD MMMM, YYYY');
    },
  },
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────── */
.detail-layout { display: grid; grid-template-columns: 1fr 320px; gap: 2rem; padding: 2.5rem 0; align-items: start; }
.detail-main { display: flex; flex-direction: column; gap: 1.5rem; }
.detail-aside { display: flex; flex-direction: column; gap: 1.25rem; position: sticky; top: 80px; }

/* ── Cards ───────────────────────────────────────────── */
.sc-card { background: #fff; border-radius: var(--radius-lg); border: 1px solid var(--border-light); box-shadow: var(--shadow-sm); }
.sc-card-header { padding: 1rem 1.25rem; border-bottom: 1px solid var(--border-light); font-weight: 700; font-size: 1rem; color: var(--text); }
.sc-card-body { padding: 1.25rem; }

/* ── Title bar ───────────────────────────────────────── */
.title-bar { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.title-bar-left { flex: 1; }
.title-bar-right { display: flex; align-items: center; gap: .75rem; flex-shrink: 0; flex-wrap: wrap; }
.detail-title { font-size: 1.5rem; font-weight: 800; color: var(--text); margin: 0 0 .5rem; line-height: 1.3; }
.detail-meta { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; font-size: .875rem; color: var(--text-muted); }
.meta-loc { display: flex; align-items: center; gap: .25rem; }
.meta-date { color: var(--text-muted); }

/* ── Service-specific meta ───────────────────────────── */
.svc-deadline { display: inline-flex; align-items: center; gap: .3rem; font-size: .8125rem; color: #f59e0b; font-weight: 500; }

/* ── Tabs ────────────────────────────────────────────── */
.pd-tabs-bar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.pd-tabs-bar .filter-tabs { flex: 1; border-bottom: none; }
.filter-tabs { display: flex; gap: .25rem; padding: .75rem 1rem; }
.filter-tab { padding: .5rem 1.125rem; border: none; border-radius: var(--radius-full); background: var(--surface-3); color: var(--text-muted); font-size: .875rem; font-weight: 500; cursor: pointer; font-family: inherit; transition: all .15s; }
.filter-tab.active { background: var(--primary); color: #fff; }
.filter-tab:hover:not(.active) { background: var(--surface-2); color: var(--text); }
.pd-tab-count { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; padding: 0 4px; margin-left: 5px; border-radius: 9px; background: var(--primary, #1e3a5f); color: #fff; font-size: .7rem; font-weight: 700; line-height: 1; }

/* ── Details grid ────────────────────────────────────── */
.details-grid { display: flex; flex-direction: column; }
.detail-row { display: flex; padding: .75rem 0; border-bottom: 1px solid var(--border-light); gap: 1rem; }
.detail-row:last-child { border-bottom: none; }
.detail-label { font-weight: 600; font-size: .875rem; color: var(--text-muted); min-width: 160px; flex-shrink: 0; }
.detail-value { font-size: .9375rem; color: var(--text); }

/* ── Prose ───────────────────────────────────────────── */
.prose-content { font-size: .9375rem; line-height: 1.7; color: var(--text); }

/* ── Applicants ──────────────────────────────────────── */
.applicants-list { display: flex; flex-direction: column; gap: 1rem; }
.applicant-item { display: flex; align-items: center; gap: .875rem; padding: .75rem; border-radius: 8px; border: 1px solid var(--border, #e5e7eb); transition: background .15s; }
.applicant-item:hover { background: var(--surface-2, #f8fafc); }
.applicant-avatar img { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.applicant-info { display: flex; flex-direction: column; gap: .2rem; }
.applicant-name { font-weight: 600; font-size: .9375rem; color: var(--text, #111827); text-decoration: none; }
.applicant-name:hover { color: var(--primary, #1e3a5f); text-decoration: underline; }
.applicant-role { font-size: .8125rem; color: var(--text-muted, #6b7280); }
.applicant-loc { display: inline-flex; align-items: center; gap: .25rem; font-size: .8rem; color: var(--text-muted, #9ca3af); }

/* ── Author card ─────────────────────────────────────── */
.author-card { background: #fff; border-radius: var(--radius-lg); border: 1px solid var(--border-light); box-shadow: var(--shadow-sm); padding: 1.25rem; display: flex; flex-direction: column; align-items: center; text-align: center; gap: .75rem; }
.author-avatar { width: 72px; height: 72px; border-radius: 50%; overflow: hidden; background: var(--surface-3); }
.author-avatar img { width: 100%; height: 100%; object-fit: cover; }
.author-name { font-weight: 700; font-size: 1rem; color: var(--text); }
.author-role { font-size: .875rem; color: var(--text-muted); }
.author-loc { display: flex; align-items: center; justify-content: center; gap: .3rem; font-size: .8125rem; color: var(--text-muted, #9ca3af); margin-bottom: .5rem; }
.author-actions { display: flex; gap: .5rem; width: 100%; }
.author-actions a { flex: 1; text-align: center; }

/* ── Apply card ──────────────────────────────────────── */
.svc-apply-prompt { font-size: .875rem; color: var(--text-muted, #6b7280); margin: 0 0 .75rem; }
.svc-applied-btn { width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: .4rem; color: #059669; border-color: #059669; }

/* ── Ads ─────────────────────────────────────────────── */
.ad-card { margin-bottom: .75rem; }
.ad-card:last-child { margin-bottom: 0; }
.ad-img { width: 100%; border-radius: var(--radius); }

/* ── Modals ──────────────────────────────────────────── */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 500; display: flex; align-items: flex-start; justify-content: center; padding: 2rem 1rem; overflow-y: auto; }
.modal-panel { background: #fff; border-radius: var(--radius-lg); box-shadow: var(--shadow-xl); width: 100%; max-width: 700px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--border-light); }
.modal-header h2 { font-size: 1.25rem; font-weight: 700; margin: 0; }
.modal-close { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: var(--text-muted); line-height: 1; padding: 0; }
.modal-body { padding: 1.5rem; max-height: 70vh; overflow-y: auto; }
.modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--border-light); display: flex; justify-content: flex-end; gap: .75rem; }

/* ── Form fields ─────────────────────────────────────── */
.sc-field { display: flex; flex-direction: column; gap: .375rem; margin-bottom: 1rem; }
.sc-field label { font-size: .875rem; font-weight: 600; color: #334155; }
.sc-field input, .sc-field select, .sc-field textarea { width: 100%; padding: .625rem .875rem; border: 1.5px solid var(--border); border-radius: var(--radius); font-family: inherit; font-size: .9375rem; color: var(--text); background: #fff; outline: none; transition: border-color .15s; box-sizing: border-box; }
.sc-field input:focus, .sc-field select:focus, .sc-field textarea:focus { border-color: var(--primary); }
.form-section-label { font-size: .8125rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: var(--text-muted); margin: 1rem 0 .5rem; }
.fields-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; }
.req { color: var(--danger); }

/* ── Misc ────────────────────────────────────────────── */
.no-content { color: var(--text-muted); font-size: .9375rem; text-align: center; padding: 2rem 0; }
.sc-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Responsive ──────────────────────────────────────── */
@media (max-width: 768px) {
  .detail-layout { grid-template-columns: 1fr; }
  .detail-aside { position: static; }
  .fields-row { grid-template-columns: 1fr; }
  .title-bar { flex-direction: column; }
  .detail-label { min-width: 120px; }
}

:deep(.ql-editor) { min-height: 160px; }
</style>
